import React from 'react';
import { Clock, ArrowLeft, Rocket, Zap, Target, Users } from 'lucide-react';

const ComingSoon = ({ featureName, featureId, onBack }) => {
  const getFeatureDetails = (id) => {
    const features = {
      'assignment-usage': {
        icon: <Users size={48} style={{ color: '#10b981' }} />,
        title: 'Assignment & Usage',
        description: 'Track asset assignments, usage patterns, and user interactions with comprehensive analytics and reporting.',
        benefits: [
          'Real-time assignment tracking',
          'Usage analytics and insights',
          'User behavior analysis',
          'Resource optimization recommendations'
        ],
        timeline: 'Q2 2024'
      },
      'maintenance-ops': {
        icon: <Zap size={48} style={{ color: '#f59e0b' }} />,
        title: 'Maintenance & Operations',
        description: 'Streamline maintenance workflows, schedule operations, and track performance metrics with intelligent automation.',
        benefits: [
          'Automated maintenance scheduling',
          'Predictive maintenance alerts',
          'Operations workflow management',
          'Performance monitoring dashboards'
        ],
        timeline: 'Q2 2024'
      },
      'financial-valuation': {
        icon: <Target size={48} style={{ color: '#ef4444' }} />,
        title: 'Financial & Valuation',
        description: 'Comprehensive financial tracking, asset valuation, cost analysis, and ROI calculations with advanced reporting.',
        benefits: [
          'Real-time asset valuation',
          'Cost tracking and analysis',
          'ROI and performance metrics',
          'Financial reporting and insights'
        ],
        timeline: 'Q3 2024'
      },
      'contract-lifecycle': {
        icon: <Rocket size={48} style={{ color: '#8b5cf6' }} />,
        title: 'Contract Lifecycle',
        description: 'End-to-end contract lifecycle management from creation to renewal with automated workflows and compliance tracking.',
        benefits: [
          'Automated lifecycle workflows',
          'Compliance monitoring',
          'Renewal management',
          'Performance tracking'
        ],
        timeline: 'Q2 2024'
      },
      'ai-insights': {
        icon: <Rocket size={48} style={{ color: '#f59e0b' }} />,
        title: 'AI Insights',
        description: 'Advanced AI-powered insights including risk scoring, anomaly detection, and intelligent summaries for better decision making.',
        benefits: [
          'Intelligent risk assessment',
          'Anomaly detection alerts',
          'Automated summaries',
          'Predictive insights'
        ],
        timeline: 'Q2 2024'
      },
      'ai-contract-drafting': {
        icon: <Rocket size={48} style={{ color: '#f59e0b' }} />,
        title: 'AI Contract Drafting',
        description: 'AI-powered contract drafting with intelligent clause suggestions, legal compliance checking, and automated document generation.',
        benefits: [
          'Smart clause suggestions',
          'Legal compliance checking',
          'Automated document generation',
          'Template optimization'
        ],
        timeline: 'Q2 2024'
      },
      'ai-search-qa': {
        icon: <Rocket size={48} style={{ color: '#f59e0b' }} />,
        title: 'AI Search & Q&A',
        description: 'Intelligent search capabilities with natural language processing and AI-powered Q&A for instant contract and asset insights.',
        benefits: [
          'Natural language search',
          'Intelligent Q&A system',
          'Context-aware results',
          'Smart recommendations'
        ],
        timeline: 'Q2 2024'
      },
      'predictive-analytics': {
        icon: <Rocket size={48} style={{ color: '#f59e0b' }} />,
        title: 'Predictive Analytics',
        description: 'Advanced predictive analytics for contract performance, asset utilization, and business intelligence with machine learning models.',
        benefits: [
          'Predictive modeling',
          'Performance forecasting',
          'Risk prediction',
          'Business intelligence'
        ],
        timeline: 'Q3 2024'
      },
      'reports': {
        icon: <Rocket size={48} style={{ color: '#ef4444' }} />,
        title: 'Reports & Analytics',
        description: 'Comprehensive reporting suite with customizable dashboards, advanced analytics, and automated report generation.',
        benefits: [
          'Customizable dashboards',
          'Advanced analytics',
          'Automated reporting',
          'Data visualization'
        ],
        timeline: 'Q2 2024'
      },
      'settings': {
        icon: <Rocket size={48} style={{ color: '#6b7280' }} />,
        title: 'System Settings',
        description: 'Comprehensive system configuration, user management, security settings, and integration management.',
        benefits: [
          'System configuration',
          'User management',
          'Security settings',
          'Integration management'
        ],
        timeline: 'Q2 2024'
      }
    };
    
    return features[id] || {
      icon: <Rocket size={48} style={{ color: '#007B8F' }} />,
      title: featureName || 'Feature',
      description: 'This feature is currently under development and will be available soon.',
      benefits: [
        'Enhanced functionality',
        'Improved user experience',
        'Advanced capabilities',
        'Better performance'
      ],
      timeline: 'Coming Soon'
    };
  };

  const feature = getFeatureDetails(featureId);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
    }}>
      <div style={{
        maxWidth: '800px',
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        padding: '48px',
        textAlign: 'center'
      }}>
        {/* Back Button */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-start', 
          marginBottom: '32px' 
        }}>
          <button
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.15s ease-in-out'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#f3f4f6';
            }}
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
        </div>

        {/* Feature Icon */}
        <div style={{ marginBottom: '24px' }}>
          {feature.icon}
        </div>

        {/* Coming Soon Badge */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          backgroundColor: '#fef3c7',
          color: '#92400e',
          borderRadius: '20px',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: '24px'
        }}>
          <Clock size={16} />
          Coming Soon
        </div>

        {/* Feature Title */}
        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#1f2937',
          margin: '0 0 16px 0',
          lineHeight: '1.2'
        }}>
          {feature.title}
        </h1>

        {/* Feature Description */}
        <p style={{
          fontSize: '18px',
          color: '#6b7280',
          lineHeight: '1.6',
          margin: '0 0 32px 0',
          maxWidth: '600px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          {feature.description}
        </p>

        {/* Benefits List */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '16px',
          marginBottom: '32px',
          textAlign: 'left'
        }}>
          {feature.benefits.map((benefit, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}
            >
              <div style={{
                width: '6px',
                height: '6px',
                backgroundColor: '#007B8F',
                borderRadius: '50%',
                flexShrink: 0
              }} />
              <span style={{
                fontSize: '14px',
                color: '#374151',
                fontWeight: '500'
              }}>
                {benefit}
              </span>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div style={{
          padding: '16px 24px',
          backgroundColor: '#f0f9ff',
          borderRadius: '12px',
          border: '1px solid #bae6fd',
          marginBottom: '32px'
        }}>
          <div style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#0369a1',
            marginBottom: '4px'
          }}>
            Expected Release
          </div>
          <div style={{
            fontSize: '14px',
            color: '#0284c7'
          }}>
            {feature.timeline}
          </div>
        </div>

        {/* Call to Action */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: 0
          }}>
            Want to be notified when this feature is available?
          </p>
          <button
            style={{
              padding: '12px 24px',
              backgroundColor: '#007B8F',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.15s ease-in-out'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#005f73';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#007B8F';
            }}
          >
            Notify Me When Available
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComingSoon;
